import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wheel } from "react-custom-roulette";
import "./Game.css";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { API_URL } from "../config";
import { useUser } from "../context/UserContext";
import { FaTicketAlt, FaGem } from "react-icons/fa";
import { useTitle } from "../hooks/useTitle";
import haptic from "../utils/haptic";

import ReactGA from "react-ga4";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { showAd } from "../components/MonetagHelper";
import AdSense from "react-adsense";
import AnimatedCounter from "../components/AnimatedCounter";
import "react-toastify/dist/ReactToastify.css";

const data = []; // Will be populated from server

function SpinGame() {
  useTitle("Lucky Gems Spin Game");
  const navigate = useNavigate();
  const { userData, setUserData, APIKey } = useUser();
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [winningMsg, setWinningMsg] = useState("");
  const [spinCount, setSpinCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [recentWins, setRecentWins] = useState([]);
  const [prizeData, setPrizeData] = useState([]);
  const pendingPrize = useRef(null); // Store prize result until wheel stops

  const GetMoreTicket = () => {
    WebApp.showPopup(
      {
        title: "Out of Tickets",
        message:
          "You have run out of tickets. Please visit the store to get more tickets.",
        buttons: [
          { id: "Tasks", type: "default", text: "Complete Tasks" },
          {
            id: "go_to_store",
            type: "default",
            text: "Get more tickets",
          },
        ],
      },
      (buttonId) => {
        if (buttonId === "go_to_store") {
          navigate("/store");
        } else if (buttonId === "Tasks") {
          navigate("/tasks");
        }
      }
    );
  };

  function onStopSpinning() {
    const prize = prizeData[prizeNumber];
    setMustSpin(false);
    haptic.success();

    // Apply the prize to the user's balance NOW (when wheel lands)
    if (pendingPrize.current) {
      const { points, tickets } = pendingPrize.current;
      setSpinCount(tickets);
      setUserData(prev => ({
        ...prev,
        user: { ...prev.user, points, tickets }
      }));
      pendingPrize.current = null;
    }

    // Set winning message based on prize type
    if (prize.type === "gems") {
      const msg = `You won ${prize.amount} Gems!`;
      setWinningMsg(msg);
      setRecentWins(prev => [{ id: Date.now(), text: msg, type: 'gems' }, ...prev].slice(0, 30));
    } else if (prize.type === "tickets") {
      const msg = `You won ${prize.amount} Ticket${prize.amount > 1 ? 's' : ''}!`;
      setWinningMsg(msg);
      setRecentWins(prev => [{ id: Date.now(), text: msg, type: 'tickets' }, ...prev].slice(0, 30));
    } else {
      setRecentWins(prev => [{ id: Date.now(), text: `No Win!`, type: 'loss' }, ...prev].slice(0, 30));
    }

    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  }

  // Fetch prize config and sync spin count from context
  useEffect(() => {
    setIsLoading(true);
    axios.get(`${API_URL}/api/game/prizes`, {
      params: { initData: WebApp.initData },
      headers: { "x-api-key": APIKey }
    }).then(res => {
      setPrizeData(res.data);
    }).catch(err => console.error("Failed to fetch prizes", err))
    .finally(() => setIsLoading(false));
  }, []);

  // Load spin history from localStorage on mount (keep last 24h only)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("spinHistory");
      if (stored) {
        const parsed = JSON.parse(stored);
        const oneDayAgo = Date.now() - 86400000;
        const fresh = parsed.filter(w => w.id > oneDayAgo);
        setRecentWins(fresh);
      }
    } catch (e) {
      localStorage.removeItem("spinHistory");
    }
  }, []);

  // Save spin history to localStorage whenever it changes
  useEffect(() => {
    if (recentWins.length > 0) {
      localStorage.setItem("spinHistory", JSON.stringify(recentWins));
    }
  }, [recentWins]);

  // Sync spin count from context userData
  useEffect(() => {
    if (userData.user?.tickets !== undefined) {
      setSpinCount(userData.user.tickets);
    }
  }, [userData.user?.tickets]);

  const handleSpinClick = async () => {
    haptic.tap();
    ReactGA.event({
      category: "Spin",
      action: "Make a spin",
      label: "Game",
    });

    // Show ad before spinning and wait for it to finish
    await showAd();

    setWinningMsg("");

    try {
      // Call server to execute spin
      const response = await axios.post(
        `${API_URL}/api/game/spin`,
        {
          userId: userData.userId,
          initData: WebApp.initData
        },
        { headers: { "x-api-key": APIKey } }
      );

      if (response.data.success) {
        const { prizeIndex, user } = response.data;
        setPrizeNumber(prizeIndex);
        setMustSpin(true);

        // Deduct the spin cost from UI immediately (1 ticket used)
        const optimisticTickets = (userData.user?.tickets || 1) - 1;
        setSpinCount(optimisticTickets);
        setUserData(prev => ({
          ...prev,
          user: { ...prev.user, tickets: optimisticTickets }
        }));

        // Store the final balance — applied when the wheel stops
        pendingPrize.current = { points: user.points, tickets: user.tickets };
      }
    } catch (error) {
      console.error("Spin failed:", error);
      haptic.error();
      if (error.response?.data?.error === "Insufficient tickets") {
        GetMoreTicket();
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="bg-slate-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-black text-slate-900 dark:text-white min-h-screen pb-24 transition-colors duration-300"
    >


      <div className="container mx-auto px-4 pt-20 pb-8">
        {/* Odds teaser */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-4 flex-wrap"
        >
          <span className="flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400">
            Win up to {prizeData.filter(p => p.type === "gems").reduce((max, p) => Math.max(max, p.amount || 0), 0) || "??"}
            <FaGem size={12} />
          </span>
          <span className="text-slate-300 dark:text-slate-600">•</span>
          <span>or</span>
          <span className="flex items-center gap-1 font-semibold text-red-500 dark:text-red-400">
            {prizeData.filter(p => p.type === "tickets").reduce((max, p) => Math.max(max, p.amount || 0), 0) || "??"} Tickets
            <FaTicketAlt size={12} />
          </span>
          <span className="text-slate-300 dark:text-slate-600">•</span>
          <span className="flex items-center gap-1">costs 1 <FaTicketAlt size={11} className="text-red-400" /> per spin</span>
        </motion.div>
        <div className="flex flex-col items-center mt-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="wheel-container mb-6 relative rounded-full shadow-[0_0_60px_rgba(245,158,11,0.3)] ring-4 ring-amber-500/20"
          >
            <Wheel
              mustStartSpinning={mustSpin}
              prizeNumber={prizeNumber}
              data={prizeData.length > 0 ? prizeData : [{ option: "Loading..." }]}
              backgroundColors={[
                "#4f46e5", // Indigo 600
                "#9333ea", // Purple 600
                "#c026d3", // Fuchsia 600
                "#e11d48", // Rose 600
                "#ea580c", // Orange 600
                "#d97706", // Amber 600
                "#16a34a", // Green 600
                "#0d9488", // Teal 600
              ]}
              textColors={["#ffffff"]}
              outerBorderColor="#FFD700"
              outerBorderWidth={5}
              innerRadius={20}
              innerBorderColor="#FFD700"
              innerBorderWidth={5}
              radiusLineColor="#FFD700"
              radiusLineWidth={2}
              fontSize={18}
              perpendicularText={true}
              textDistance={65}
              onStopSpinning={onStopSpinning}
            />
          </motion.div>
          <AnimatePresence>
            {winningMsg && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="mb-6 px-6 py-4 rounded-2xl shadow-lg border-2 flex items-center gap-3 bg-white dark:bg-slate-800 border-amber-300 dark:border-amber-500/40"
              >
                {winningMsg.includes("Ticket") ? (
                  <FaTicketAlt className="text-red-500 dark:text-red-400 shrink-0" size={22} />
                ) : (
                  <FaGem className="text-amber-500 dark:text-amber-400 shrink-0" size={22} />
                )}
                <span className="text-xl font-bold text-amber-700 dark:text-amber-300">{winningMsg}</span>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Supercharged Spin Console */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm bg-white dark:bg-slate-800/80 backdrop-blur-md rounded-3xl p-5 mt-2 mb-8 shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-slate-200 dark:border-slate-700 flex items-center justify-between"
          >
            <div className="flex flex-col items-start gap-1">
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-extrabold uppercase tracking-widest pl-1">Your Balance</span>
              <div className="flex items-center gap-2 font-black text-3xl text-slate-800 dark:text-white">
                <FaTicketAlt className="text-red-500 drop-shadow-sm" />
                <AnimatedCounter value={spinCount} />
              </div>
              {spinCount === 0 && (
                <button
                  onClick={() => navigate("/store")}
                  className="text-teal-600 dark:text-teal-400 text-xs font-semibold underline mt-1 pl-1"
                >
                  Visit Store
                </button>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`bg-gradient-to-br from-[#9333EA] to-[#a855f7] text-white py-4 px-8 rounded-2xl shadow-lg shadow-purple-500/40 transition-all flex flex-col items-center justify-center leading-none ${
                mustSpin || spinCount === 0 ? "opacity-50 cursor-not-allowed grayscale" : ""
              }`}
              onClick={spinCount > 0 ? handleSpinClick : GetMoreTicket}
              disabled={mustSpin || spinCount === 0}
            >
              <span className="text-2xl font-black uppercase tracking-widest text-white drop-shadow-md">
                {isLoading ? "..." : "Spin"}
              </span>
              <span className="text-sm font-bold text-purple-100 mt-1.5 flex items-center gap-1.5 opacity-90 tracking-wide">
                -1 <FaTicketAlt size={14} />
              </span>
            </motion.button>
          </motion.div>

          <div className="w-full px-4 mx-auto mb-6">
            <AdSense.Google
              client="ca-pub-4252052358641832"
              slot="4385377268"
              style={{ display: "block" }}
              format="auto"
              responsive="true"
              layoutKey="-gw-1+2a-9x+5c"
            />
          </div>

          {/* Recent Wins Section */}
          {recentWins.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 w-full mx-auto"
            >
              <div className="flex items-center gap-2 mb-3 px-4">
                <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex-shrink-0">Live Feed</h3>
                <div className="h-px w-full bg-slate-200 dark:bg-slate-700/50" />
              </div>

              {/* Marquee Wrapper */}
              <div className="overflow-hidden whitespace-nowrap w-full relative">
                {/* 
                  To create a seamless infinite loop, we render the array twice. 
                  If the array is very small (e.g. 1 item), we render it more times to ensure it fills the width.
                */}
                <div className="animate-infinite-scroll flex gap-3 px-4">
                  {[...recentWins, ...recentWins, ...recentWins, ...recentWins].slice(0, Math.max(recentWins.length * 2, 8)).map((win, index) => (
                    <div
                      key={`${win.id}-${index}`}
                      className={`shrink-0 px-4 py-2.5 rounded-xl border-2 flex items-center shadow-sm backdrop-blur bg-white/50 dark:bg-slate-800/50 ${
                        win.type === 'gems' ? 'border-amber-300 text-amber-600 dark:border-amber-400/30 dark:text-amber-300' :
                        win.type === 'tickets' ? 'border-red-300 text-red-600 dark:border-red-400/30 dark:text-red-300' :
                        'border-slate-200 text-slate-500 dark:border-slate-600/30 dark:text-slate-400'
                      }`}
                    >
                      <span className="font-semibold text-sm whitespace-nowrap">{win.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Fading Edges */}
                <div className="absolute top-0 left-0 w-8 h-full bg-gradient-to-r from-slate-50 dark:from-gray-900 to-transparent pointer-events-none" />
                <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-slate-50 dark:from-gray-900 to-transparent pointer-events-none" />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default SpinGame;
